import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  ArrowLeft,
  MapPin,
  Phone,
  Star,
  Calendar,
  CheckCircle2,
  Briefcase,
  User,
  Quote,
  Shield,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Separate Stars component
const Stars = ({ rating }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-0.5">
      {stars.map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating
              ? "fill-[#D4AF37] text-[#D4AF37]"
              : "fill-slate-200 text-slate-200"
          }`}
        />
      ))}
    </div>
  );
};

// Separate Review Item component
const ReviewItem = ({ review }) => {
  return (
    <div className="p-6" data-testid={`review-${review.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-slate-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-[#0B1F3B]">{review.customer_name}</span>
            <span className="text-sm text-slate-400">•</span>
            <span className="text-sm text-slate-500">{formatDate(review.created_at)}</span>
          </div>
          <div className="mb-2">
            <Stars rating={review.rating} />
          </div>
          <p className="text-slate-600">{review.comment}</p>
        </div>
      </div>
    </div>
  );
};

// Reviews List component
const ReviewsList = ({ reviews }) => {
  const { t } = useTranslation();
  if (reviews.length === 0) {
    return (
      <div className="p-12 text-center">
        <Quote className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h3 className="font-semibold text-[#0B1F3B] mb-2">{t("publicProfile.noReviews")}</h3>
        <p className="text-slate-500">{t("publicProfile.noReviewsDesc")}</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {reviews.map((review) => (
        <ReviewItem key={review.id} review={review} />
      ))}
    </div>
  );
};

export default function BusinessPublicProfile() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    fetchBusiness();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const fetchBusiness = async () => {
    try {
      let response;
      try {
        response = await axios.get(`${API}/business/public/${businessId}`);
      } catch (e) {
        if (e.response?.status === 404) {
          response = await axios.get(`${API}/business/public/user/${businessId}`);
        } else {
          throw e;
        }
      }
      setBusiness(response.data);
    } catch (error) {
      toast.error(t("publicProfile.businessNotFound"));
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!business) return null;

  const reviewCount = business.review_count || 0;
  const avgRating = business.average_rating || 0;
  const serviceCategories = business.service_categories || [];
  const reviews = business.reviews || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <span className="font-bold text-[#0B1F3B]">MyProjectBidder</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8" data-testid="business-public-profile">
        {/* Business Header Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Business Avatar */}
            <div className="w-20 h-20 rounded-xl bg-[#0B1F3B] flex items-center justify-center flex-shrink-0">
              <span className="text-3xl font-bold text-[#D4AF37]">
                {business.business_name.charAt(0)}
              </span>
            </div>
            
            {/* Business Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
                  {business.business_name}
                </h1>
                {business.is_verified && (
                  <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="verified-badge">
                    <Shield className="w-3 h-3 mr-1" />
                    {t("publicProfile.verifiedContractor")}
                  </Badge>
                )}
              </div>
              
              {/* Rating */}
              <div className="flex items-center gap-2 mb-3">
                <Stars rating={Math.round(avgRating)} />
                <span className="font-semibold text-[#0B1F3B]">{avgRating}</span>
                <span className="text-slate-500">({reviewCount} {t("publicProfile.reviews")})</span>
              </div>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {business.city}, {business.zip_code}
                </span>
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4 text-slate-400" />
                  {business.completed_projects} {t("publicProfile.projectsCompleted")}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {t("publicProfile.memberSince")} {formatDate(business.member_since)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#D4AF37]" />
                <span className="font-medium text-[#0B1F3B]">{business.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-slate-600">{t("publicProfile.owner")}: {business.owner_name}</span>
              </div>
            </div>
          </div>
        </div>

        {/* About & Services */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* About */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-bold text-[#0B1F3B] mb-4">{t("publicProfile.about")}</h2>
            <p className="text-slate-600 whitespace-pre-wrap">{business.description}</p>
          </div>
          
          {/* Services */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-bold text-[#0B1F3B] mb-4">Services Offered</h2>
            <div className="flex flex-wrap gap-2">
              {serviceCategories.map((category, idx) => (
                <Badge
                  key={idx}
                  className="bg-[#D4AF37]/10 text-[#0B1F3B] border border-[#D4AF37]"
                >
                  {category}
                </Badge>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                <MapPin className="w-4 h-4 inline mr-1" />
                {t("publicProfile.serviceArea")}: {business.service_radius} {t("publicProfile.milesFrom")} {business.city}
              </p>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#0B1F3B]">
                {t("publicProfile.customerReviews")} ({reviewCount})
              </h2>
              <div className="flex items-center gap-2">
                <Stars rating={Math.round(avgRating)} />
                <span className="font-semibold">{avgRating} {t("publicProfile.avg")}</span>
              </div>
            </div>
          </div>
          <ReviewsList reviews={reviews} />
        </div>
      </main>
    </div>
  );
}
